import * as React from "react"
import { useI18n } from '../../contexts/I18nContext';

// Static months for parsing (locale-neutral values — kept for parseDateString)
const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

// Generate years from 1950 to current year + 5
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1950 + 6 }, (_, i) => (currentYear + 5 - i).toString());

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  showMonth?: boolean;
  showPresent?: boolean;
  className?: string;
}

// Parse a date string in various formats to extract month and year
function parseDateString(dateStr: string): { month: string; year: string } {
  if (!dateStr) return { month: '', year: '' };
  
  // Handle "Présent" or similar
  if (dateStr.toLowerCase().includes('présent') || dateStr.toLowerCase().includes('present') || dateStr.toLowerCase().includes('actuel')) {
    return { month: 'present', year: 'present' };
  }
  
  // Try to extract month and year from various formats
  const monthNames: Record<string, string> = {
    'janvier': '01', 'janv': '01', 'jan': '01',
    'février': '02', 'fevrier': '02', 'févr': '02', 'fév': '02', 'fev': '02',
    'mars': '03', 'mar': '03',
    'avril': '04', 'avr': '04', 'apr': '04',
    'mai': '05', 'may': '05',
    'juin': '06', 'jun': '06',
    'juillet': '07', 'juil': '07', 'jul': '07',
    'août': '08', 'aout': '08', 'aug': '08',
    'septembre': '09', 'sept': '09', 'sep': '09',
    'octobre': '10', 'oct': '10',
    'novembre': '11', 'nov': '11',
    'décembre': '12', 'decembre': '12', 'déc': '12', 'dec': '12',
  };
  
  const lower = dateStr.toLowerCase().trim();
  let month = '';
  let year = '';
  
  // Find month
  for (const [name, num] of Object.entries(monthNames)) {
    if (lower.includes(name)) {
      month = num;
      break;
    }
  }
  
  // Find year (4 digits)
  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/);
  if (yearMatch) {
    year = yearMatch[0];
  }
  
  return { month, year };
}

// Format month and year to a display string (uses static French labels for stored values)
function formatDate(month: string, year: string): string {
  if (month === 'present' || year === 'present') {
    return 'Présent';
  }
  
  if (!year) return '';
  
  const monthLabel = MONTHS.find(m => m.value === month)?.label || '';
  
  if (monthLabel && year) {
    return `${monthLabel} ${year}`;
  }
  
  return year;
}

export function DatePicker({
  value,
  onChange,
  placeholder: _placeholder = "Date",
  showMonth = true,
  showPresent = false,
  className = ""
}: DatePickerProps) {
  const { t } = useI18n();
  const { month, year } = parseDateString(value);
  const isPresent = month === 'present' || year === 'present';

  const MONTH_LABELS: Record<string, string> = {
    '01': t('datePicker.jan'), '02': t('datePicker.feb'), '03': t('datePicker.mar'),
    '04': t('datePicker.apr'), '05': t('datePicker.may'), '06': t('datePicker.jun'),
    '07': t('datePicker.jul'), '08': t('datePicker.aug'), '09': t('datePicker.sep'),
    '10': t('datePicker.oct'), '11': t('datePicker.nov'), '12': t('datePicker.dec'),
  };

  const handleMonthChange = (newMonth: string) => {
    if (newMonth === 'present') {
      onChange('Présent');
    } else {
      onChange(formatDate(newMonth, year === 'present' ? '' : year));
    }
  };

  const handleYearChange = (newYear: string) => {
    if (newYear === 'present') {
      onChange('Présent');
    } else {
      onChange(formatDate(month === 'present' ? '' : month, newYear));
    }
  };

  return (
    <div className={`flex gap-1 ${className}`}>
      {showMonth && (
        <select
          value={isPresent ? 'present' : month}
          onChange={(e) => handleMonthChange(e.target.value)}
          className="flex-1 bg-black/40 border border-white/10 text-white text-[10px] rounded-lg px-2 py-1.5 focus:border-blue-500/50 focus:outline-none"
        >
          <option value="">{t('datePicker.month')}</option>
          {MONTHS.map(m => (
            <option key={m.value} value={m.value}>{MONTH_LABELS[m.value] ?? m.label}</option>
          ))}
          {showPresent && <option value="present">{t('datePicker.present')}</option>}
        </select>
      )}
      <select
        value={isPresent ? 'present' : year}
        onChange={(e) => handleYearChange(e.target.value)}
        className="flex-1 bg-black/40 border border-white/10 text-white text-[10px] rounded-lg px-2 py-1.5 focus:border-blue-500/50 focus:outline-none"
      >
        <option value="">{t('datePicker.year')}</option>
        {showPresent && <option value="present">{t('datePicker.present')}</option>}
        {YEARS.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}

// Simple year-only picker for education
export function YearPicker({
  value,
  onChange,
  className = ""
}: Omit<DatePickerProps, 'showMonth' | 'showPresent'>) {
  const { t } = useI18n();
  const { year } = parseDateString(value);

  return (
    <select
      value={year}
      onChange={(e) => onChange(e.target.value)}
      className={`bg-black/40 border border-white/10 text-white text-[10px] rounded-lg px-2 py-1.5 focus:border-blue-500/50 focus:outline-none ${className}`}
    >
      <option value="">{t('datePicker.year')}</option>
      {YEARS.map(y => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { MONTHS, YEARS, parseDateString, formatDate };
